import { useContext, createContext, ReactNode, memo } from 'react';
import { Tournament } from 'functions/src/types/firestore/Game/Tournament';
import { HeatsBracket } from './HeatsBracketTab';
import { useHeatTabs } from 'src/hooks/useHeatTabs';

export type BracketContextType = Pick<
  Tournament<Date>,
  'id' | 'gameId' | 'sponsors'
> & {
  heatTabs: (
    | {
        name: string;
        content: JSX.Element;
      }
    | undefined
  )[];
  isDoubleElimination: boolean;
};

export const BracketContext = createContext<BracketContextType | null>(null);

export const useBracket = () => {
  const context = useContext(BracketContext);
  if (!context) {
    throw new Error('useBracket must be used within a BracketProvider');
  }
  return context;
};

export const BracketProvider: React.FC<
  Pick<HeatsBracket, 'heats'> & {
    tournament: Tournament<Date>;
    children: ReactNode;
  }
> = memo(function BracketProvider({ children, tournament, heats }) {
  const { id, gameId, sponsors, bracketType } = tournament;
  const isDoubleElimination = bracketType === 'double-elimination';

  const { heatTabs } = useHeatTabs({
    heats,
    isDoubleElimination,
  });

  const bracketData = {
    id,
    gameId,
    heatTabs,
    sponsors,
    isDoubleElimination,
  };
  return (
    <BracketContext.Provider value={{ ...bracketData }}>
      {children}
    </BracketContext.Provider>
  );
});
